import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import './MUICustomDataGridStyles.css'; 
import { ThemeProvider, createTheme } from '@mui/material/styles';

/**
 * @name AltDataGridPro
 * @description A wrapper around the DataGridPro component from MUI
 * @param {Object} props - The properties passed to the component
 * @returns A JSX element containing the AltDataGridPro component
 *       with the specified properties
 * */

import { GridOverlay } from "@mui/x-data-grid-pro";
import { Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

// Custom No Data overlay
const CustomNoRowsOverlay = () => {
    document.querySelector(".MuiDataGrid-root")?.classList.add("no-data"); // Add class when no data
    return (
        <GridOverlay>
            <Typography variant="body1" sx={{ mt: 2 }}>
                No Data Available
            </Typography>
        </GridOverlay>
    );
};


const AltDataGridPro = ({
    columns,
    rows,
    loading,
    pageSize,
    onPageSizeChange,
    onRowClick,
    checkboxSelection,
    disableSelectionOnClick,
    paginationMode,
    hideFooter = true,
    sortingMode,
    onRowSelectionModelChange,
    rowSelectionModel,
    onSortModelChange,
    rowHeight,
    onCellClick,
    styleProps,
    headerHeight,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
    disableRowSelectionOnClick,
    rowReordering,
    onRowOrderChange,
    localeText,
    isRowSelectable,
    disableColumnMenu,
    filterBy, // { column: "columnName", value: "filterValue" }
    ...otherProps
}) => {
    const noRowsLabel = "No Data Available";
    const noResultsOverlayLabel = "No Data Available";

    const [filteredRows, setFilteredRows] = useState(rows);

    // State to manage the filter model
    const [filterModel, setFilterModel] = useState({
        items: filterBy
            ? [{ field: filterBy.column, operator: "contains", value: filterBy.value }]
            : [],
    });

    useEffect(() => {
        if (filterBy) {
            setFilterModel({
                items: [{ field: filterBy.column, operator: "contains", value: filterBy.value }],
            });
        }
    }, [filterBy]);


    //To Do : Remove this post MUI License
    useEffect(() => {
        // Find all divs with the text "Open new page"
        const divs = Array.from(document.querySelectorAll("div"));
        divs.forEach((div) => {
            if (div.textContent === "MUI X Missing license key") {
                div.textContent = ""; // Replace the content
            }
        });
    }, [rows]);

    // Update filtered rows dynamically
    useEffect(() => {
        if (!filterModel.items.length) {
            setFilteredRows(rows);
            return;
        }

        const { field, value } = filterModel.items[0];

        const newFilteredRows = value !== undefined ? rows.filter(row =>
            row[field]?.toString()?.toLowerCase().includes(value?.toLowerCase())
        ) : rows;

        setFilteredRows(newFilteredRows);
    }, [filterModel, rows]);

    useEffect(() => {
        if (rows.length === 0) {
            document.querySelector(".MuiDataGrid-root")?.classList.add("no-data"); // Add class when no data
        }
    }, [rows])

    const dataGridTheme = createTheme({
        components: {
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#fff',
                    },
                    columnHeaders: {
                        backgroundColor: '#fff',
                    },
                },
            },
        },
    });

    return (
        <ThemeProvider theme={dataGridTheme}>
            <DataGrid
                rows={filteredRows} // Use dynamically filtered rows
                columns={columns?.map(col => ({ ...col, filterable: true }))}
                loading={loading}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                onRowClick={onRowClick}
                checkboxSelection={checkboxSelection}
                disableSelectionOnClick={disableSelectionOnClick}
                paginationMode={paginationMode}
                sortingMode={sortingMode}
                sx={{
                    backgroundColor: "#fff", 
                    "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-virtualScrollerContent": {
                        overflow: filteredRows.length === 0 ? "hidden !important" : "auto !important",
                    },
                    "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#f0f6ff", // Change to your desired hover color
                        cursor: "pointer"
                    }
                }}
                hideFooter={hideFooter}
                filterModel={filterModel} // Controlled filter model
                filterMode="client"
                initialState={{ filterModel: filterModel }} // Set initial filter model
                onFilterModelChange={(newModel) => setFilterModel(newModel)} // Keep UI in sync
                onCellClick={onCellClick}
                isRowSelectable={isRowSelectable}
                disableRowSelectionOnClick={disableRowSelectionOnClick}
                disableMultipleRowSelection={true}
                rowSelectionModel={rowSelectionModel}
                onSortModelChange={onSortModelChange}
                onRowSelectionModelChange={onRowSelectionModelChange}
                rowHeight={rowHeight}
                headerHeight={headerHeight}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                rowReordering={rowReordering}
                onRowOrderChange={onRowOrderChange}
                localeText={{
                    ...localeText,
                    noRowsLabel,
                    noResultsOverlayLabel,
                    MuiTablePagination: {
                        labelRowsPerPage: 'Rows Per Page:',
                    },
                }}
                components={{
                    NoRowsOverlay: () => <CustomNoRowsOverlay filteredRows={filteredRows} />,
                }}
                disableColumnMenu={disableColumnMenu}
                {...otherProps}
            />
        </ThemeProvider>
    );
};

// Default props
AltDataGridPro.defaultProps = {
    rows: [],
    columns: [],
    loading: false,
    pageSize: 5,
    hideFooter: true,
    checkboxSelection: false,
    disableSelectionOnClick: true,
    paginationMode: "client",
    sortingMode: "client",
    rowHeight: 52,
    headerHeight: 56,
};

// Prop types
AltDataGridPro.propTypes = {
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    pageSize: PropTypes.number,
    onPageSizeChange: PropTypes.func,
    onRowClick: PropTypes.func,
    checkboxSelection: PropTypes.bool,
    hideFooter: PropTypes.bool,
    disableSelectionOnClick: PropTypes.bool,
    paginationMode: PropTypes.oneOf(["client", "server"]),
    sortingMode: PropTypes.oneOf(["client", "server"]),
    onSortModelChange: PropTypes.func,
    rowHeight: PropTypes.number,
    headerHeight: PropTypes.number,
    columnVisibilityModel: PropTypes.object,
    onColumnVisibilityModelChange: PropTypes.func,
    rowReordering: PropTypes.bool,
    onRowOrderChange: PropTypes.func,
    localeText: PropTypes.object,
    disableColumnMenu: PropTypes.bool,
    filterBy: PropTypes.shape({
        column: PropTypes.string,
        value: PropTypes.any,
    }),
};

export default AltDataGridPro;
